import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import { Translate as T } from 'react-redux-i18n';
import { i18n } from '@international/mastodon-i18n';
import employmentIcon from '../../../images/icons/documents/Employment.svg';
import { readPolicy } from './utils/formatter';
import SimplePanel from '../../Panels/SimplePanel';
import SimpleDocumentList from '../../fields/SimpleDocumentList';
import secureFolderIcon from '../../../images/icons/documents/SecureFolder.svg';
import {
  CircleParameters,
  CircleProps,
  DateComponentProps,
  DocumentListSectionProps,
  DocumentUploadHeaderProps,
  GenerateDateComponentsProps,
  ModalFooterProps,
} from './utils/interfaces';

const DEFAULT_CIRCLE_PARAMETERS: CircleParameters = {
  cx: 75,
  cy: 75,
  radius: 60,
  strokeWidth: 15,
  stroke: '#E1E6EB',
};

const PROGRESS_CIRCLE_COLOR = '#009CB8';

const Circle: React.FC<CircleProps> = ({ circleParameters, progress = 0 }) => {
  const { cx, cy, radius, strokeWidth, stroke } = circleParameters;
  const circumference = 2 * Math.PI * radius;
  const offset = (1 - progress) * circumference;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill='none'
      strokeDasharray={circumference}
      strokeDashoffset={offset}
      strokeLinecap='round'
      transform='rotate(-90 75 75)'
    />
  );
};

const DateComponent: React.FC<DateComponentProps> = ({
  date,
  endDate,
  keyName,
  uploaded,
}) => (
  <>
    <M.Icon
      style={{ marginRight: 0 }}
      icon={uploaded ? 'CheckmarkFilled' : 'Document'}
      className={uploaded ? 'status-clear' : 'checkr-blue'}
    />
    &nbsp;
    <span className='text-panel'>
      {i18n.getStr(
        `components.Verification.EmploymentMultidocPanel.${keyName}`,
        { date, endDate },
      )}
    </span>
    <br />
    <br />
  </>
);

const DocumentListSection: React.FC<DocumentListSectionProps> = ({
  documents,
  additionalDocuments,
  onRemoveByUploadId,
}) => (
  <SimplePanel>
    <div className='spaced-items'>
      <M.GridRow className='flex-center'>
        <M.GridCol md={12} className='no-padding'>
          <img src={secureFolderIcon} alt='Employment Proof' />
        </M.GridCol>
        <M.GridCol>
          <DocumentSectionTitle
            titleKey='components.employment.uploadedDocuments'
            reviewKey=''
          />
        </M.GridCol>
      </M.GridRow>
      <M.GridRow>
        <M.GridCol className='padded-left'>
          <DocumentSectionTitle
            titleKey='components.employment.requiredDocuments'
            reviewKey='components.employment.requiredDocumentsReview'
            headingLevel='h3'
          />
        </M.GridCol>
      </M.GridRow>
      <M.GridRow>
        <M.GridCol className='padded-left'>
          <SimpleDocumentList
            onRemoveByUploadId={onRemoveByUploadId}
            documents={documents}
            useTagStyle
          />
        </M.GridCol>
      </M.GridRow>
      {additionalDocuments.length > 0 && (
        <>
          <M.GridRow>
            <M.GridCol className='padded-left'>
              <DocumentSectionTitle
                titleKey='components.employment.otherDocuments'
                reviewKey='components.employment.otherDocumentsReview'
                headingLevel='h3'
              />
            </M.GridCol>
          </M.GridRow>
          <M.GridRow>
            <M.GridCol className='padded-left'>
              <SimpleDocumentList
                onRemoveByUploadId={onRemoveByUploadId}
                documents={additionalDocuments}
                useTagStyle
              />
            </M.GridCol>
          </M.GridRow>
        </>
      )}
    </div>
  </SimplePanel>
);

const DocumentUploadHeader: React.FC<DocumentUploadHeaderProps> = ({
  position,
  company,
  startDate,
  endDate,
}) => (
  <div className='text-center document-upload-header'>
    <h4 className='job-title'>
      <span className='company-position'>
        <span className='company'>{company}</span>
        <span className='position'>{position}</span>
      </span>
    </h4>
    <h5 className='text-muted text-bold'>{`${startDate} - ${endDate}`}</h5>
  </div>
);

const GenerateDateComponents: React.FC<GenerateDateComponentsProps> = ({
  documentsRequired,
  policy,
  endDate,
}) => {
  return (
    <>
      {documentsRequired.map((year: any, i: number) => (
        <DateComponent
          key={`document-${year.value}`}
          date={year.value}
          endDate={endDate}
          keyName={readPolicy(
            policy,
            i,
            year.value === endDate,
            documentsRequired.length,
          )}
          uploaded={year.disabled}
        />
      ))}
    </>
  );
};

const generateDateComponentsToString = (
  documentsRequired: any[],
  policy: any,
  endDate: string,
) => {
  return documentsRequired
    .reduce((acc, year, index) => {
      if (year.disabled) return acc;
      const template = i18n.getStr(
        `components.Verification.EmploymentMultidocPanel.${readPolicy(
          policy,
          index,
          year.value === endDate,
          documentsRequired.length,
        )}`,
        { date: year.value, endDate },
      );
      acc.push(`<li style="margin-left: 1em;">&nbsp;${template}</li>`);
      return acc;
    }, [] as string[])
    .join('\n');
};

const DocumentSectionTitle: React.FC<{
  titleKey: string;
  reviewKey: string;
  headingLevel?: 'h2' | 'h3';
  reviewContent?: string;
}> = ({ titleKey, reviewKey, headingLevel = 'h2', reviewContent }) => {
  const HeadingTag = headingLevel;
  return (
    <>
      <HeadingTag>
        <T value={titleKey} className='text-panel' dangerousHTML />
      </HeadingTag>
      <T
        value={reviewKey}
        reviewContent={reviewContent}
        className='text-panel'
        dangerousHTML
      />
    </>
  );
};

const ModalHeader: React.FC<ModalFooterProps> = ({ onCancel, textContent }) => (
  <div className='modal-header'>
    <div id='left-column'>
      <h2>{textContent}</h2>
    </div>
    <div id='right-column'>
      <button onClick={onCancel} className='close-button' type='button'>
        &times;
      </button>
    </div>
  </div>
);

const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  handleSubmit,
}) => (
  <div className='modal-footer'>
    <M.Button onClick={onCancel} kind='secondary'>
      {i18n.getStr('buttons.cancel')}
    </M.Button>
    <M.Button onClick={handleSubmit} kind='primary'>
      {i18n.getStr('buttons.confirm')}
    </M.Button>
  </div>
);

const UploadCircleWrapper: React.FC<{ progress: number }> = ({ progress }) => {
  return (
    <div className='circle-wrapper' aria-label='progress circle'>
      <svg width='150' height='150'>
        <Circle circleParameters={DEFAULT_CIRCLE_PARAMETERS} progress={1} />
        <Circle
          circleParameters={{
            ...DEFAULT_CIRCLE_PARAMETERS,
            stroke: PROGRESS_CIRCLE_COLOR,
          }}
          progress={progress}
        />
      </svg>
      <img
        src={employmentIcon}
        alt='Employment Proof'
        className='centered-image'
      />
    </div>
  );
};

export {
  Circle,
  DateComponent,
  DocumentListSection,
  DocumentSectionTitle,
  DocumentUploadHeader,
  GenerateDateComponents,
  generateDateComponentsToString,
  ModalFooter,
  ModalHeader,
  UploadCircleWrapper,
};
